import React, { useCallback, useEffect, useMemo, useState } from 'react'

import * as UserEvents from 'redux/api/internal/UserEvents'
import { Action, Context, ENavDestination } from 'types/GlobalUserEvents'
import { addRef } from 'utils/queryHandler'

import { debounce as _debounce } from 'lodash'

import { useHistory } from 'react-router-dom'
import { useTheme } from '@mui/material'

import {
  useAvailableDashboardGroupsIncludeDashboards,
  useDashboardGroupsLoading
} from 'redux/hooks/DashboardGroups'
import { useAvailableMetrics } from '../MetricsTable/utils'
import { useCustomer } from 'redux/hooks/Customer'
import { useEntities } from 'redux/hooks/Entities'
import { useGroups } from 'redux/hooks/Groups'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useKeyMetrics } from 'redux/hooks/KeyMetrics'
import { useMetrics } from 'redux/hooks/Metrics'
import { useReports } from 'redux/hooks/Reports'
import { useTranslation } from 'language/translator'

import { TKeyMetric } from 'types/GlobalKeyMetric'

import Box from 'components_new/atoms/Box'
import Emoji from 'components_new/atoms/Emoji'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import InputBase from 'components_new/atoms/InputBase'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem, {
  GenericListItemProps
} from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import { getPages } from './lib'
import { joinWithAnd } from 'utils/functions'
import { WhoCanView } from 'types/GlobalDashboardGroup'
import { getContextFromPath } from 'utils/contextPaths'

const flattenKeyMetrics = (items: TKeyMetric[]): TKeyMetric[] =>
  items.flatMap((km) => [km, ...flattenKeyMetrics(km.children)])

interface GlobalSearchProps {
  onClose: () => void
  // when opened with keystrokes from inside component
  onOpen: () => void
  open: boolean
}

const GlobalSearch = (props: GlobalSearchProps) => {
  const { onClose, onOpen, open } = props

  const history = useHistory()
  const { lang, t } = useTranslation()
  const theme = useTheme()

  const context = getContextFromPath(history.location.pathname)

  const customer = useCustomer()

  const dashboardGroups = useAvailableDashboardGroupsIncludeDashboards()
  const dashboardGroupsLoading = useDashboardGroupsLoading()
  const [entities, entitiesLoading] = useEntities()
  const { isAdmin, isCompanyGroup } = useAccountSettings()
  const [keyMetrics, keyMetricsFetched] = useKeyMetrics()
  const [metrics, metricsLoading] = useMetrics()
  const availableMetrics = useAvailableMetrics(metrics, false, null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { myGroups, loading: groupsLoading } = useGroups()
  const reports = useReports()

  // access
  const allowKeyMetrics = Boolean(customer?.allow_access.allow_key_metrics)
  const includeEntities = isAdmin
  const includeGroups = !isCompanyGroup
  const includeKeyMetrics = allowKeyMetrics && !isCompanyGroup
  const includeMetrics = isAdmin || (allowKeyMetrics && !isCompanyGroup)
  // $TODO: change to allow_reports:
  const includeReports = Boolean(customer?.allow_access.allow_key_metrics)

  // loading
  const keyMetricsLoading = includeKeyMetrics && !keyMetricsFetched
  const loading =
    dashboardGroupsLoading ||
    entitiesLoading ||
    keyMetricsLoading ||
    metricsLoading ||
    groupsLoading

  // search states
  const [help, setHelp] = useState<boolean>(false)
  const [input, setInput] = useState<string>('')
  const [search, setSearch] = useState<string | null>(null)

  const noSearch = !search || search?.length <= 2

  const helperText = useMemo(() => {
    const accessableItems: string[] = [
      t('global', 'pages', true),
      ...(includeKeyMetrics ? [t('key_metrics', 'key_metrics', true)] : []),
      t('global', 'dashboards', true),
      ...(includeReports ? [t('global', 'reports', true)] : []),
      ...(includeGroups ? [t('global', 'groups', true)] : []),
      ...(includeMetrics ? [t('metrics', 'metrics', true)] : []),
      ...(includeEntities ? [t('entities', 'entities', true)] : [])
    ]

    return (
      t('search', 'search_for') +
      ' ' +
      joinWithAnd({ t: t, values: accessableItems }) +
      '.'
    )
  }, [
    // access
    includeGroups,
    includeEntities,
    includeKeyMetrics,
    includeMetrics,
    includeReports,
    // translation
    t
  ])

  // available items
  const searchableItems: (Omit<GenericListItemProps, 'title' | 'to'> & {
    destination: ENavDestination
    title: string
    titleLower: string
    to: string
    variantTitle: string
  })[] = useMemo(() => {
    if (loading) return []

    // 1) pages
    const p = getPages({
      access: {
        entities: includeEntities,
        groups: includeGroups,
        keyMetrics: includeKeyMetrics,
        metrics: includeMetrics,
        reports: includeReports,
        tasks: includeKeyMetrics
      },
      t: t
    })

    // 2) groups
    const g = !includeGroups
      ? []
      : myGroups.map((group) => {
        return {
          color: group.profile_color,
          title: group.name,
          titleIcon: group.is_private
            ? { name: 'Private' as const }
            : undefined,
          titleLower: group.name.toLocaleLowerCase(),
          to: `/groups/${group.id}`,
          variant: 'group' as const,
          variantTitle: t('groups', 'group'),
          destination: ENavDestination.PAGE_GROUP
        }
      })

    // 3) reports
    const r = reports.map((r) => {
      return {
        title: r.title,
        titleLower: r.title.toLocaleLowerCase(),
        to: `/reports/${r}`,
        variant: 'report' as const,
        variantTitle: t('global', 'report'),
        destination: ENavDestination.PAGE_DASHBOARD
      }
    })

    // 4) dashboards
    const d = dashboardGroups.map((dashboardGroup) => {
      return {
        title: dashboardGroup.title,
        titleIcon:
          dashboardGroup.share_settings.who_can_view ===
          WhoCanView.ONLY_ADDED_USERS
            ? { name: 'Private' as const }
            : undefined,
        titleLower: dashboardGroup.title.toLocaleLowerCase(),
        to: `/dashboards/${dashboardGroup.firstDashboardId}`,
        variant: 'dashboard' as const,
        variantTitle: t('global', 'dashboard'),
        destination: ENavDestination.PAGE_DASHBOARD
      }
    })

    // 5) dashboard tabs
    const dt = dashboardGroups.flatMap((dashboardGroup) => {
      return dashboardGroup.dashboards.map((dashboard) => {
        return {
          title: dashboard.title,
          titleLower: dashboard.title.toLocaleLowerCase(),
          to: `/dashboards/${dashboard.id}`,
          variant: 'tab' as const,
          variantTitle: `${t('global', 'dashboard')} / ${t(
            'dashboards',
            'tab'
          )}`,
          destination: ENavDestination.PAGE_DASHBOARD
        }
      })
    })

    // 6) key metrics
    const km = !includeKeyMetrics
      ? []
      : flattenKeyMetrics(keyMetrics).map((keyMetric) => {
        return {
          title: keyMetric.title,
          titleLower: keyMetric.title.toLocaleLowerCase(),
          to: `/key-metrics/${keyMetric.id}`,
          variant: 'key_metric' as const,
          variantTitle: t('key_metrics', 'key_metric'),
          destination: ENavDestination.PAGE_KEY_METRIC
        }
      })

    // 7) metrics
    const m = !includeMetrics
      ? []
      : availableMetrics.map((metric) => {
        return {
          title: metric.title[lang],
          titleIcon: metric.access.private
            ? { name: 'Private' as const }
            : undefined,
          titleLower: metric.title[lang].toLocaleLowerCase(),
          to: `/metrics/${metric.id}`,
          variant: 'metric' as const,
          variantTitle: t('metrics', 'metric'),
          destination: ENavDestination.PAGE_METRIC
        }
      })

    // 8) entities
    const e = !isAdmin
      ? []
      : entities.map((entity) => {
        return {
          title: entity.title[lang],
          titleLower: entity.title[lang].toLocaleLowerCase(),
          to: `/entities/${entity.id}`,
          variant: 'entity' as const,
          variantTitle: t('entities', 'entity'),
          destination: ENavDestination.PAGE_ENTITY
        }
      })

    return [...p, ...g, ...r, ...d, ...dt, ...km, ...m, ...e]
  }, [
    loading,
    // access
    includeEntities,
    includeGroups,
    includeKeyMetrics,
    includeMetrics,
    isAdmin,
    // items
    dashboardGroups,
    entities,
    keyMetrics,
    metrics,
    myGroups,
    reports,
    // translation
    lang,
    t
  ])

  // filtered items from search
  const searchLower = search?.toLowerCase() ?? ''
  const filteredItems = useMemo(() => {
    if (noSearch) return []

    return searchableItems.filter((item) => {
      return item.titleLower.includes(searchLower)
    })
  }, [search, searchableItems, searchLower])

  // handle search
  const debouncedSetQuery = useMemo(
    () =>
      _debounce((value: string | null) => {
        setSearch(value)
      }, 500),
    [setSearch]
  )

  const handleSearch = useCallback(
    (value: string) => {
      setInput(value)
      debouncedSetQuery(value === '' ? null : value)
    },
    [setSearch, debouncedSetQuery]
  )

  const handleClear = useCallback(() => {
    setInput('')
    debouncedSetQuery(null)
  }, [setSearch, debouncedSetQuery])

  useEffect(() => {
    if (help) setHelp(false)

    if (search && filteredItems.length > 0) {
      setActive(0)
    }
  }, [search])

  const handleClose = () => {
    onClose()
    handleClear()
  }

  // handle open and close
  const handleUserClose = useCallback(() => {
    // log close event
    UserEvents.log({
      action: Action.GLOBAL_SEARCH_CLOSED,
      context,
      input: search
    })
    handleClose()
  }, [context, search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const isTyping =
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          (el as any).isContentEditable)

      const k = e.key ? e.key.toLowerCase() : undefined
      const isCmdOrCtrl = (e.metaKey && !e.ctrlKey) || (e.ctrlKey && !e.metaKey)

      // Cmd/Ctrl+K toggles open/close
      if (isCmdOrCtrl && k === 'k') {
        e.preventDefault()

        if (open) {
          handleUserClose()
        } else if (!isTyping) {
          onOpen()
        }

        return
      }

      // "/" when not typing (no modifiers)
      if (!isTyping && !e.metaKey && !e.ctrlKey && !e.altKey && k === '/') {
        e.preventDefault()
        onOpen()
      }
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpen, handleUserClose])

  // cleanup
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel()
    }
  }, [debouncedSetQuery])

  // log open event
  useEffect(() => {
    if (open) {
      UserEvents.log({
        action: Action.GLOBAL_SEARCH_OPENED,
        context: context
      })
    }
  }, [open])

  // handle active item
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const itemRefs = React.useRef<Array<HTMLElement | null>>([])
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)

  const resultsId = 'SearchResultsListbox'

  const setActive = (i: number) => {
    if (i < 0 || i > filteredItems.length - 1) return

    setActiveIndex(i)
    // Keep the active item visible without moving focus
    itemRefs.current[i]?.scrollIntoView({ block: 'nearest' })
  }

  const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (filteredItems.length === 0) {
      if (e.key === 'Enter') {
        setHelp(true)
      }

      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(activeIndex + 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(activeIndex - 1)
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        e.preventDefault()

        handleNavigateToActive()
      }
    }
  }

  const handleNavigateToActive = () => {
    requestAnimationFrame(() => {
      const el = document.activeElement as HTMLElement | null

      if (el?.classList?.contains('SearchNavigationItem')) el.blur()
    })

    const navItem = filteredItems[activeIndex]
    const href = addRef(navItem.to, Context.FROM_GLOBAL_SEARCH)

    // log navigation event
    UserEvents.log({
      action: Action.GLOBAL_SEARCH_NAVIGATED,
      context: context,
      input: search,
      type: navItem.destination
    })

    history.push(href)
    handleClose()
  }

  const onItemMouseMove = useCallback((i: number) => setActiveIndex(i), [])
  const onItemClick = useCallback(
    () => handleNavigateToActive(),
    [handleNavigateToActive]
  )

  return (
    <SimpleDialog
      dividers={true}
      onClose={handleUserClose}
      open={open}
      PaperProps={{
        sx: {
          maxHeight: 'calc(100% - 2 * 32px)'
        }
      }}
      sx={{
        ['& .MuiDialog-container']: {
          alignItems: 'flex-start',
          height: '100vh',
          py: '15vh'
        }
      }}
      titleHero={
        <InputBase
          autoFocus={true}
          aria-expanded={!!filteredItems.length}
          aria-controls={resultsId}
          aria-activedescendant={
            activeIndex >= 0 ? `search-option-${activeIndex}` : undefined
          }
          disabled={loading}
          fullWidth={true}
          inputProps={{
            autocomplete: 'off',
            style: {
              fontSize: theme.typography.h5['fontSize']
            }
          }}
          inputRef={inputRef}
          placeholder={
            loading
              ? t('global', 'loading') + '...'
              : t('search', 'global_search_placeholder') + '...'
          }
          margin="none"
          name="search"
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleInputKeyDown}
          role="combobox"
          size="medium"
          endAdornment={
            <IconButton
              disabled={loading}
              onClick={() => {
                if (noSearch) setHelp(true)
              }}
            >
              {loading ? <LoadingSpinner size={24} /> : <Icon name="Search" />}
            </IconButton>
          }
          value={input}
        />
      }
    >
      {noSearch ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text color="text.secondary" variant="body2">
            {helperText}
          </Text>

          <Text color={help ? 'error.text' : 'text.disabled'} variant="body2">
            {'(' + t('search', 'global_search_instructions') + ')'}
          </Text>
        </Box>
      ) : null}

      {!noSearch && filteredItems.length === 0 ? (
        <EmptyPlaceholder
          background={true}
          height={128}
          size="small"
          title={
            <>
              {t('search', 'no_results_match')} &quot;{search}&quot;.{' '}
              <Emoji name={'mag'} />
            </>
          }
        />
      ) : null}

      <Box role="listbox" id={resultsId} aria-label="Search results">
        {filteredItems.map((item, i) => {
          const { color, icon, title, titleIcon, variant, variantTitle } = item

          const isActive = i === activeIndex

          return (
            <GenericListItem
              aria-selected={isActive}
              chip={{ label: variantTitle }}
              color={color}
              customTitle={<Highlighted highlight={search} text={title} />}
              icon={icon}
              id={`search-option-${i}`}
              key={`${item.variant}-${item.id}`}
              onClick={onItemClick}
              onMouseMove={() => onItemMouseMove(i)}
              ref={(el: HTMLElement | null) => {
                itemRefs.current[i] = el
              }}
              role="option"
              sx={{
                bgcolor: isActive ? 'action.focus' : undefined,
                ['&:hover']: {
                  bgcolor: isActive ? 'action.focus' : 'transparent'
                }
              }}
              tabIndex={-1}
              title={title}
              titleIcon={titleIcon}
              variant={variant}
            />
          )
        })}
      </Box>
    </SimpleDialog>
  )
}

export default GlobalSearch
